import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import FreelancerCard, { FreelancerCardSkeleton } from '../components/FreelancerCard';
import AdBanner from '../components/AdBanner';
import Breadcrumb from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Search, RotateCcw, SlidersHorizontal, ChevronDown, X, ChevronsDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOBILE_CLASSES = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
const DESKTOP_CLASSES = {
  4: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  5: 'sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
  6: 'sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
};
const buildGridClass = (mobile, desktop) =>
  `grid ${MOBILE_CLASSES[mobile] || 'grid-cols-3'} ${DESKTOP_CLASSES[desktop] || DESKTOP_CLASSES[6]} gap-2 sm:gap-4`;

const SearchPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState(searchParams.get('q') || '');
  const [siteSettings, setSiteSettings] = useState(null);
  const [visibleCount, setVisibleCount] = useState(24);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || 'all');
  const [selectedGender, setSelectedGender] = useState(searchParams.get('gender') || 'all');
  const [selectedEyeColor, setSelectedEyeColor] = useState(searchParams.get('eyes') || 'all');
  const [selectedHairColor, setSelectedHairColor] = useState(searchParams.get('hair') || 'all');
  const [selectedBodyType, setSelectedBodyType] = useState(searchParams.get('body') || 'all');
  const [selectedHeight, setSelectedHeight] = useState(searchParams.get('height') || 'all');
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || 'all');

  // Dynamic options from backend
  const [attributes, setAttributes] = useState({
    categories: [],
    cities: [],
    genders: [],
    services: [],
    eye_colors: [],
    hair_colors: [],
    body_types: [],
    heights: []
  });

  useEffect(() => {
    fetchAttributes();
    fetchListings();
    fetchSiteSettings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedCategory, selectedCity, selectedGender, selectedEyeColor, selectedHairColor, selectedBodyType, selectedHeight, selectedService, allListings]);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSiteSettings(response.data);
      setVisibleCount(Number(response.data.items_per_page) || 24);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchAttributes = async () => {
    try {
      const response = await axios.get(`${API}/settings/attributes`);
      setAttributes(response.data);
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/listings`);
      setAllListings(response.data);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      setAllListings([]);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allListings];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(l => l.category_slug === selectedCategory || l.category === selectedCategory);
    }
    if (selectedCity !== 'all') {
      filtered = filtered.filter(l => l.city?.toLowerCase().includes(selectedCity.toLowerCase()));
    }
    if (selectedGender !== 'all') {
      filtered = filtered.filter(l => l.gender === selectedGender);
    }
    if (selectedEyeColor !== 'all') {
      filtered = filtered.filter(l => l.eye_color === selectedEyeColor);
    }
    if (selectedHairColor !== 'all') {
      filtered = filtered.filter(l => l.hair_color === selectedHairColor);
    }
    if (selectedBodyType !== 'all') {
      filtered = filtered.filter(l => l.body_type === selectedBodyType);
    }
    if (selectedHeight !== 'all') {
      filtered = filtered.filter(l => l.height === selectedHeight);
    }
    if (selectedService !== 'all') {
      filtered = filtered.filter(l => l.services?.includes(selectedService));
    }

    setListings(filtered);
  };

  const handleSearch = () => {
    if (!searchKeyword.trim()) {
      applyFilters();
      return;
    }

    const keyword = searchKeyword.toLowerCase();
    const filtered = allListings.filter(l =>
      l.name?.toLowerCase().includes(keyword) ||
      l.title?.toLowerCase().includes(keyword) ||
      l.description?.toLowerCase().includes(keyword) ||
      l.skills?.some(s => s.toLowerCase().includes(keyword))
    );
    setListings(filtered);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const resetFilters = () => {
    setSearchKeyword('');
    setSelectedCategory('all');
    setSelectedCity('all');
    setSelectedGender('all');
    setSelectedEyeColor('all');
    setSelectedHairColor('all');
    setSelectedBodyType('all');
    setSelectedHeight('all');
    setSelectedService('all');
    setSearchParams({});
    setVisibleCount(Number(siteSettings?.items_per_page) || 24);
  };

  const hasFilters = searchKeyword ||
    selectedCategory !== 'all' ||
    selectedCity !== 'all' ||
    selectedGender !== 'all' ||
    selectedEyeColor !== 'all' ||
    selectedHairColor !== 'all' ||
    selectedBodyType !== 'all' ||
    selectedHeight !== 'all' ||
    selectedService !== 'all';

  const categories = attributes.categories || [];
  const cities = attributes.cities || [];
  const genders = attributes.genders || [];
  const eyeColors = attributes.eye_colors || [];
  const hairColors = attributes.hair_colors || [];
  const bodyTypes = attributes.body_types || [];
  const heights = attributes.heights || [];
  const services = attributes.services || [];

  // Compute sidebar visibility and grid columns from settings
  const computeLayout = () => {
    const mobileCols = Number(siteSettings?.mobile_grid_columns) || 3;
    if (!siteSettings) return { showSidebar: false, gridClass: buildGridClass(mobileCols, 6) };

    const adPages = siteSettings.ad_pages || {};
    const pageEnabled = adPages.search !== false;
    const ads = siteSettings.ads || [];
    const leftAd = ads.find(a => a.position === 'left');
    const rightAd = ads.find(a => a.position === 'right');
    const hasActiveSidebar = pageEnabled && (
      (leftAd && leftAd.active === true) || (rightAd && rightAd.active === true)
    );

    if (hasActiveSidebar) {
      return { showSidebar: true, gridClass: buildGridClass(mobileCols, 4), leftAd, rightAd, pageEnabled };
    }

    const cols = Number(siteSettings.grid_columns) || 6;
    return { showSidebar: false, gridClass: buildGridClass(mobileCols, cols), leftAd, rightAd, pageEnabled };
  };

  const { showSidebar, gridClass, leftAd, rightAd, pageEnabled } = computeLayout();

  // Sidebar ad renderer
  const renderSidebarAd = (ad) => {
    if (!ad || !ad.active || !pageEnabled) return null;
    return (
      <div className="hidden lg:block w-40 xl:w-48 flex-shrink-0">
        <div className="sticky top-24 space-y-4">
          <div className="rounded-lg overflow-hidden border border-zinc-700">
            {ad.image_url ? (
              ad.link_url ? (
                <a href={ad.link_url} target="_blank" rel="noopener noreferrer">
                  <img src={ad.image_url} alt="Advertisement" className="w-full" />
                </a>
              ) : (
                <img src={ad.image_url} alt="Advertisement" className="w-full" />
              )
            ) : (
              <div className="h-[600px] bg-gradient-to-b from-zinc-800 via-zinc-700 to-zinc-800 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-zinc-400 text-sm">AD</p>
                  <p className="text-zinc-500 text-xs">160x600</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const pageTitle = selectedCategory !== 'all'
    ? `${categories.find(c => c.id === selectedCategory)?.name || selectedCategory} - ${t('search.search')}`
    : `${t('search.search')} - `;
  const pageDesc = selectedCity !== 'all'
    ? `${selectedCity} ${t('search.resultsFound')} - `
    : t('search.searchPlaceholder');

  return (
    <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.origin + '/search'} />
      </Helmet>

      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: t('header.search'), href: null }]} />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('search.search')}</h1>
        </div>
      </div>

      {/* Search Controls */}
      <div className="border-b border-zinc-800 py-6 bg-zinc-950">
        <div className="container mx-auto px-4">

          {/* Keyword Search */}
          <div className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder={t('search.searchPlaceholder')}
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={handleKeyDown}
                className="bg-zinc-900 border-zinc-700 text-zinc-300 h-11 pl-10"
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              className="bg-emerald-600 hover:bg-emerald-500 h-11 px-6 transition-all hover:shadow-lg hover:shadow-emerald-500/20"
              data-testid="search-button"
            >
              {t('search.search')}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-zinc-800 py-4 bg-zinc-900/50">
        <div className="container mx-auto px-4">
          {/* Mobile filter toggle */}
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 text-zinc-300 font-medium mb-3 sm:hidden"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t('search.filters')}
            {hasFilters && <span className="w-2 h-2 bg-emerald-500 rounded-full" />}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className={`flex-wrap items-center gap-2 sm:gap-3 ${filtersOpen ? 'flex' : 'hidden sm:flex'}`}>
            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-40 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="category-filter">
                <SelectValue placeholder={t('listing.allCategories')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all" className="text-zinc-300">{t('listing.allCategories')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id} className="text-zinc-300">
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* City Filter */}
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-36 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="city-filter">
                <SelectValue placeholder={t('listing.allCities')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all" className="text-zinc-300">{t('listing.allCities')}</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.name} className="text-zinc-300">
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Gender Filter */}
            {genders.length > 0 && (
              <Select value={selectedGender} onValueChange={setSelectedGender}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-32 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="gender-filter">
                  <SelectValue placeholder={t('listing.allGenders')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allGenders')}</SelectItem>
                  {genders.map(g => (
                    <SelectItem key={g.id} value={g.id} className="text-zinc-300">
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Eye Color Filter */}
            {eyeColors.length > 0 && (
              <Select value={selectedEyeColor} onValueChange={setSelectedEyeColor}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-32 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="eyes-filter">
                  <SelectValue placeholder={t('physicalAttributes.eyes')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allEyes')}</SelectItem>
                  {eyeColors.map(opt => (
                    <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Hair Color Filter */}
            {hairColors.length > 0 && (
              <Select value={selectedHairColor} onValueChange={setSelectedHairColor}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-32 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="hair-filter">
                  <SelectValue placeholder={t('physicalAttributes.hair')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allHair')}</SelectItem>
                  {hairColors.map(opt => (
                    <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Body Type Filter */}
            {bodyTypes.length > 0 && (
              <Select value={selectedBodyType} onValueChange={setSelectedBodyType}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-32 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="body-filter">
                  <SelectValue placeholder={t('physicalAttributes.bodyType')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allBody')}</SelectItem>
                  {bodyTypes.map(opt => (
                    <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Height Filter */}
            {heights.length > 0 && (
              <Select value={selectedHeight} onValueChange={setSelectedHeight}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-36 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="height-filter">
                  <SelectValue placeholder={t('physicalAttributes.height')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allHeights')}</SelectItem>
                  {heights.map(opt => (
                    <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Service Filter */}
            {services.length > 0 && (
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-36 bg-zinc-900 border-zinc-700 text-zinc-300 h-10" data-testid="service-filter">
                  <SelectValue placeholder={t('listing.services')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-zinc-300">{t('listing.allServices')}</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-zinc-300">
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Reset Button */}
            {hasFilters && (
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="text-zinc-400 hover:text-white h-10"
                data-testid="reset-filters"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('search.resetFilters')}
              </Button>
            )}

            {/* Results Count */}
            <div className="ml-auto text-zinc-500 text-sm font-medium">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-zinc-600 border-t-emerald-500 rounded-full animate-spin" />
                  {t('common.loading')}
                </span>
              ) : (
                <span>
                  <span className="text-white font-bold">{listings.length}</span> {t('search.resultsFound')}
                </span>
              )}
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-zinc-800/50">
              {selectedCategory !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {categories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                  <button onClick={() => setSelectedCategory('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedCity !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {selectedCity}
                  <button onClick={() => setSelectedCity('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedGender !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {genders.find(g => g.id === selectedGender)?.name || selectedGender}
                  <button onClick={() => setSelectedGender('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedEyeColor !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {eyeColors.find(e => e.id === selectedEyeColor)?.name || selectedEyeColor}
                  <button onClick={() => setSelectedEyeColor('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedHairColor !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {hairColors.find(h => h.id === selectedHairColor)?.name || selectedHairColor}
                  <button onClick={() => setSelectedHairColor('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedBodyType !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {bodyTypes.find(b => b.id === selectedBodyType)?.name || selectedBodyType}
                  <button onClick={() => setSelectedBodyType('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedHeight !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {heights.find(h => h.id === selectedHeight)?.name || selectedHeight}
                  <button onClick={() => setSelectedHeight('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedService !== 'all' && (
                <span className="animate-chip-appear inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {services.find(s => s.id === selectedService)?.name || selectedService}
                  <button onClick={() => setSelectedService('all')} className="hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                </span>
              )}
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                {t('search.resetFilters')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Top Banner Ad */}
      <AdBanner position="top" />

      {/* Main Content with Sidebar Ads */}
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar Ad */}
          {showSidebar && renderSidebarAd(leftAd)}

          {/* Results Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className={gridClass}>
                {[...Array(8)].map((_, i) => (
                  <FreelancerCardSkeleton key={i} />
                ))}
              </div>
            ) : listings.length > 0 ? (
              <>
                <div className={gridClass}>
                  {listings.slice(0, visibleCount).map((listing, index) => (
                    <FreelancerCard key={listing.id} freelancer={listing} index={index} />
                  ))}
                </div>

                {/* Load More */}
                {visibleCount < listings.length && (
                  <div className="flex flex-col items-center mt-8 gap-2">
                    <p className="text-zinc-500 text-sm">
                      {Math.min(visibleCount, listings.length)} / {listings.length} ilan gösteriliyor
                    </p>
                    <button
                      onClick={() => setVisibleCount(prev => prev + (Number(siteSettings?.items_per_page) || 24))}
                      className="flex items-center gap-2 px-8 py-3 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white border border-zinc-700 hover:border-zinc-600 transition-all font-medium"
                    >
                      <ChevronsDown className="w-5 h-5" />
                      Devamını Göster
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 text-lg mb-4">{t('search.noResults')}</p>
                <Button
                  onClick={resetFilters}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('search.resetFilters')}
                </Button>
              </div>
            )}
          </div>

          {/* Right Sidebar Ad */}
          {showSidebar && renderSidebarAd(rightAd)}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
